<template>
  <div>
    <v-dialog
      max-width="800"
      persistent
      transition="dialog-bottom-transition"
      v-model="showDialog"
    >
      <v-card :loading="loading">
        <v-toolbar dark color="primary">
          <v-btn icon dark @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>Delete Environments</v-toolbar-title>
        </v-toolbar>
        <v-card flat v-if="listShowcaseEnvironments">
          <v-card-title>
            Existing Locations
          </v-card-title>
          <v-card-text v-if="existingLocations.length > 0">
            <v-simple-table>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left">Account</th>
                    <th class="text-left">Region</th>
                    <th class="text-left"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="location in existingLocations"
                    :key="location.accountId + location.region"
                  >
                    <td>{{ location.accountId }}</td>
                    <td>
                      {{ location.region }}
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
            Multiple environemts possible for Showcase: {{ showcase.multiEnv }}
          </v-card-text>
          <v-card-text v-if="existingLocations.length === 0">
            No environments found
          </v-card-text>
        </v-card>
        <v-divider></v-divider>
        <v-card flat v-if="existingLocations.length === 0">
          <v-card-text>
            <v-alert type="warning">
              There are no environments which could be deleted.
            </v-alert>
          </v-card-text>
        </v-card>
        <v-card flat v-if="existingLocations.length > 0">
          <v-card-title>
            Select Target Locations
          </v-card-title>
          <v-card-text>
            <v-select
              class="location-select"
              v-model="locationSelect"
              :items="selectLocations"
              label="Select Locations"
              multiple
            >
            </v-select>
          </v-card-text>
        </v-card>
        <v-divider></v-divider>
        <v-card flat v-if="deleteEnvsPossible">
          <v-card-title>
            Run Options
          </v-card-title>
          <v-card-text>
            <v-row no-gutters>
              <v-col cols="3">
                <v-radio-group v-model="scheduleOrNow" :mandatory="true">
                  <v-radio label="Run now" value="now"></v-radio>
                  <v-radio label="Schedule" value="schedule"></v-radio>
                </v-radio-group>
              </v-col>
              <v-col cols="9">
                <CloudWatchCronExpressionBuilder
                  v-model="scheduleExpression"
                  v-if="schedule"
                  show-next-events="5"
                >
                </CloudWatchCronExpressionBuilder>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
        <v-card-text>
          {{ message }}
        </v-card-text>
        <v-card-actions>
          <v-btn
            @click="runOperation"
            :disabled="!deleteEnvsPossible || locationSelect.length === 0"
            >Run</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import 'vue2-dropzone/dist/vue2Dropzone.min.css'
import {
  DELETE_ENVIRONMENTS,
  SCHEDULE_DELETE_ENVIRONMENTS,
  LIST_SHOWCASE_ENVIRONMENTS
} from '@/domain/showcases/constants/query'
import { DOMAIN_NAME } from '@/domain/showcases'
import CloudWatchCronExpressionBuilder from '@/components/CWCronExpressionBuilder/CloudWatchCronExpressionBuilder'
import { isScheduleOnceType } from '@/domain/showcases/util/schedule'

export default {
  name: 'DeleteEnvironments',
  components: {
    CloudWatchCronExpressionBuilder
  },
  data: () => ({
    showDialog: true,
    loading: false,
    scheduleOrNow: 'now',
    scheduleExpression: '',
    message: null,
    locationSelect: [],
    listShowcaseEnvironments: [],
    listShowcaseEnvironmentsErrors: null
  }),
  props: ['showcase'],
  computed: {
    schedule: function() {
      return this.scheduleOrNow === 'schedule'
    },

    existingLocations: function() {
      return this.listShowcaseEnvironments
        .filter(environment => environment.status !== 'DELETED')
        .map(environment => {
          return {
            region: environment.region,
            accountId: environment.accountId
          }
        })
    },
    selectLocations() {
      return this.existingLocations.map(environment => {
        return {
          text: `${environment.accountId} : ${environment.region}`,
          value: {
            region: environment.region,
            accountId: environment.accountId
          }
        }
      })
    },
    deleteEnvsPossible() {
      return this.listShowcaseEnvironments.length > 0
    }
  },
  methods: {
    runOperation() {
      this.message = 'Starting Operation'

      let operationHandler =
        this.scheduleOrNow === 'now'
          ? this.operationHandler
          : this.scheduleOperationHandler
      this.message = 'Starting Operation'
      operationHandler()
        .then(response => {
          this.loading = false
          this.message = 'Success!'
          this.closeDialog()
        })
        .catch(err => {
          this.loading = false
          this.message = err
        })
    },
    async operationHandler() {
      return this.$apolloProvider.clients['SHOWCASE'].mutate({
        mutation: DELETE_ENVIRONMENTS,
        variables: {
          showcaseId: this.showcase.showcaseId,
          targets: this.locationSelect
        },
        fetchPolicy: 'no-cache'
      })
    },
    async scheduleOperationHandler() {
      return this.$apolloProvider.clients['SHOWCASE'].mutate({
        mutation: SCHEDULE_DELETE_ENVIRONMENTS,
        variables: {
          showcaseId: this.showcase.showcaseId,
          targets: this.locationSelect,
          scheduleExpression: 'cron(' + this.scheduleExpression + ')',
          scheduleType: isScheduleOnceType(this.scheduleExpression)
            ? 'ONCE'
            : 'PERIODIC'
        },
        fetchPolicy: 'no-cache'
      })
    },
    closeDialog() {
      this.$emit('close')
    }
  },
  apollo: {
    $client: DOMAIN_NAME,
    listShowcaseEnvironments: {
      query: LIST_SHOWCASE_ENVIRONMENTS,
      variables() {
        return { showcaseId: this.showcase.showcaseId }
      },
      skip() {
        return !this.showcase
      },
      error(error) {
        this.listShowcaseEnvironmentsErrors = JSON.stringify(error.message)
      }
    }
  }
}
</script>

<style scoped>
.location-select {
  width: 80%;
}
</style>
